import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  
  // Admin "View As" feature - allows admins to view/edit data as another user
  const [viewingAsUser, setViewingAsUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('viewingAsUserId');
    setToken(null);
    setUser(null);
    setViewingAsUser(null);
  }, []);

  // Fetch all users for admin user switcher
  const fetchAllUsers = useCallback(async () => {
    if (!token || user?.role !== 'admin') return;
    try {
      const response = await axios.get(`${API_URL}/admin/all-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [token, user?.role]);

  // Switch to viewing as another user (admin only)
  const switchUserContext = useCallback((targetUser) => {
    if (user?.role !== 'admin') {
      console.error('Only admins can switch user context');
      return;
    }
    if (targetUser) {
      setViewingAsUser(targetUser);
      localStorage.setItem('viewingAsUserId', targetUser.id);
    } else {
      // Switch back to own account
      setViewingAsUser(null);
      localStorage.removeItem('viewingAsUserId');
    }
  }, [user?.role]);

  // Clear viewing context (return to own account)
  const clearViewingContext = useCallback(() => {
    setViewingAsUser(null);
    localStorage.removeItem('viewingAsUserId');
  }, []);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          
          // Restore viewing context if admin had one
          const savedViewingId = localStorage.getItem('viewingAsUserId');
          if (response.data.role === 'admin' && savedViewingId) {
            // Fetch the user they were viewing as
            try {
              const usersResponse = await axios.get(`${API_URL}/admin/all-users`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const users = usersResponse.data.users || [];
              const targetUser = users.find(u => u.id === savedViewingId);
              if (targetUser) {
                setViewingAsUser(targetUser);
              }
              setAllUsers(users);
            } catch (e) {
              console.error('Failed to restore viewing context');
              localStorage.removeItem('viewingAsUserId');
            }
          }
        } catch (error) {
          console.error('Auth init failed:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [token, logout]);

  // Fetch users when admin logs in
  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAllUsers();
    }
  }, [user?.role, fetchAllUsers]);

  // Global axios response interceptor: any /api/* response with HTTP 402
  // and reason=billing_setup_required → bounce the user to /pricing. This
  // is the last line of defence behind the backend middleware — guarantees
  // no UI surface can silently swallow the gate.
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        try {
          if (error?.response?.status === 402) {
            const reason = error.response.data?.detail?.reason;
            if (reason === 'billing_setup_required') {
              if (typeof window !== 'undefined' &&
                  !['/pricing', '/billing/setup', '/login'].some(p => window.location.pathname.startsWith(p))) {
                window.location.assign('/pricing');
              }
            }
          }
        } catch (_) { /* never break the chain */ }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    // Clear any previous viewing context on login
    setViewingAsUser(null);
    localStorage.removeItem('viewingAsUserId');
    return userData;
  };

  // Low-level setter used by /auth/callback (native Google OAuth exchange)
  // when the backend has already minted the JWT for us. Keeps localStorage
  // + AuthContext state in sync without duplicating the login fetch logic.
  const setSession = (newToken, userData) => {
    if (!newToken) return;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setViewingAsUser(null);
    localStorage.removeItem('viewingAsUserId');
  };

  // Exchange a single-use billing-setup token (emailed to OAuth signups
  // after admin approval) for a session JWT. The user stays in
  // billing_status=pending_billing on the server until Stripe confirms,
  // but the JWT lets them reach /pricing and the checkout endpoints.
  const loginWithBillingSetupToken = async (setupToken) => {
    const response = await axios.post(`${API_URL}/auth/billing-setup`, { token: setupToken });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setViewingAsUser(null);
    localStorage.removeItem('viewingAsUserId');
    return userData;
  };

  const register = async (email, password, name, role = 'sales_rep') => {
    const response = await axios.post(`${API_URL}/auth/register`, { email, password, name, role });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  // The "effective user" is who we're viewing as (or the actual user if not viewing as anyone)
  const effectiveUser = viewingAsUser || user;
  const isViewingAsOther = viewingAsUser !== null && viewingAsUser.id !== user?.id;

  const value = {
    // Actual logged-in user
    user,
    token,
    loading,
    login,
    register,
    logout,
    loginWithBillingSetupToken,
    setSession,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isSalesRep: user?.role === 'sales_rep',
    isCreativeLead: user?.role === 'creative_lead',
    isExecutiveViewer: user?.role === 'executive_viewer',
    
    // Admin "View As" feature
    viewingAsUser,
    effectiveUser,
    isViewingAsOther,
    allUsers,
    switchUserContext,
    clearViewingContext,
    fetchAllUsers,
    
    // Helper to get the user ID that should be used for data operations
    // When admin is viewing as another user, this returns that user's ID
    getEffectiveUserId: () => effectiveUser?.id || user?.id,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
